import type { Element, Subsystem } from '../types';
import type { NoaaCatalogueEntry } from '@/features/loader/catalog/noaaCatalogue';
import { noaaPayloadReferences } from './noaaPayload';

/** Reference facts remain separate from simulated/live readings and physical part bindings. */
export function noaaReferenceElement(entry: NoaaCatalogueEntry, template?: Element): Element {
  const reference: Subsystem = {
    id: 'catalogue_reference',
    label: 'Mission catalogue and status',
    modelOnly: true,
    data: [],
    reference: {
      sourceTitle: 'NOAA catalogue review',
      sourceUrl: entry.sources[0],
      checkedAt: entry.checkedAt,
      note: `${entry.planningNote} Installed instruments do not imply current data-product availability.`,
      values: [
        { label: 'Spacecraft', value: entry.name },
        { label: 'Relationship', value: entry.role },
        { label: 'Agency / partnership', value: entry.agency },
        { label: 'Status at review', value: entry.status },
        {
          label: 'NORAD',
          value: entry.noradId == null ? 'Not assigned in this catalogue' : String(entry.noradId),
        },
        {
          label: 'Orbit regime',
          value: entry.orbitType === 'L1' ? 'Sun-Earth L1; live ephemeris not connected' : entry.orbitType,
        },
        { label: 'Model representation', value: entry.model },
      ],
    },
  };
  const generic: Subsystem[] = [
    {
      id: 'reference_payload',
      label: 'Published payload complement',
      modelOnly: true,
      data: [],
      description:
        'Instrument names are reference facts; their positions are not mapped onto the generic illustration.',
      reference: {
        sourceTitle: 'Mission payload reference',
        sourceUrl: entry.sources.at(-1)!,
        checkedAt: entry.checkedAt,
        values: entry.instruments.map((value, i) => ({ label: `Instrument ${i + 1}`, value })),
      },
    },
    ...[
      ['reference_bus', 'Illustrative spacecraft bus'],
      ['reference_wings', 'Illustrative solar wings'],
      ['reference_dish', 'Illustrative communications antenna'],
    ].map(([id, label]) => ({
      id,
      label,
      modelOnly: true,
      data: [],
      description: 'Generic visual part; not a measured component or live system.',
    })),
  ];
  return {
    ...template,
    id: `noaa_reference:${entry.id}`,
    callsign: entry.name,
    subsystems: [
      reference,
      ...(template?.subsystems ?? generic),
      // New exterior assemblies supplement the existing published payload facts.
      ...noaaPayloadReferences(entry).filter((s) => !template?.subsystems.some((t) => t.id === s.id)),
    ],
    documents: [
      ...(template?.documents ?? []),
      ...entry.sources.map((href, i) => ({
        type: 'webpage',
        label: `Catalogue source ${i + 1} (reviewed ${entry.checkedAt})`,
        href,
      })),
    ],
  };
}
